import { useState, useEffect, createContext, useContext, useRef } from 'react';
import { useReducedMotion } from '@react-spring/web';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { PERFORMANCE_EVENTS } from 'src/lib/trackingEvents';
import useHasMouse from './useHasMouse';
import { useDeviceSize } from '.';
import { isDevelopment } from 'src/lib/constants';
const PerformanceContext = createContext<boolean>(false);

export const PerformanceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const track = useTrack();
  const [isLowPerformance, setIsLowPerformance] = useState(false);
  const [performanceData, setPerformanceData] = useState<{
    isLowCpuPerformance: boolean;
    avgTime: number;
    isMobile: boolean;
  } | null>(null);

  const prefersReducedMotion = useReducedMotion();
  const hasTrackedPerformanceData = useRef(false);
  const hasMouse = useHasMouse();
  const { width } = useDeviceSize();

  useEffect(() => {
    // Lower iterations to reduce workload and memory usage.
    const iterations = 1e7;
    const trials = 5;
    let totalBenchmarkTime = 0;

    // Define a benchmark function that does some CPU-intensive work.
    const benchmark = () => {
      let total = 0;
      for (let i = 0; i < iterations; i++) {
        total += Math.sqrt(i);
      }
      return total;
    };

    // Warm-up to trigger JIT optimizations.
    benchmark();

    // Run the benchmark several times and accumulate the total time.
    for (let i = 0; i < trials; i++) {
      const start = performance.now();
      benchmark();
      totalBenchmarkTime += performance.now() - start;
    }

    const avgTime = totalBenchmarkTime / trials;
    const isMobile = width < 1024 || !hasMouse;
    const isLowCpuPerformance = isMobile
      ? /**
         * [2025-07-26] Logged averages on a homepage production build using `alert(avgTime)`
         * - Browser Stack iPhone 12: 10
         * - Real iPhone 13 Mini: 9.5
         * - Browser Stack iPhone 13 Mini: 9.5
         * - Browser Stack iPhone 14: 9.4
         * - Browser Stack iPhone 15: 8.8
         * - Browser Stack iPhone 15 Pro: 5.8
         * - Browser Stack iPhone 16: 5
         * - Browser Stack iPhone 16 Pro: 5.2
         * - Browser Stack iPhone 16 Pro Max: 5.2
         *
         * Homepage reported to crash by Plaid employees on the following devices:
         * - iPhone 13 Mini
         * - iPhone 14 Pro
         * - iPhone 15
         *
         * We can be a bit more generous with this threshold since only the homepage has 5 guillochés
         */
        avgTime > 7
      : avgTime > 11.5;

    setPerformanceData({
      avgTime,
      isMobile,
      isLowCpuPerformance: isDevelopment ? false : isLowCpuPerformance,
    });
  }, [hasMouse, width]);

  useEffect(() => {
    if (!performanceData) {
      return;
    }

    setIsLowPerformance(
      prefersReducedMotion || performanceData.isLowCpuPerformance,
    );
  }, [prefersReducedMotion, performanceData]);

  useEffect(() => {
    if (!performanceData || hasTrackedPerformanceData.current) {
      return;
    }

    hasTrackedPerformanceData.current = true;

    // NOTE: this is an example of how NOT to track
    track({
      type: performanceData.isLowCpuPerformance
        ? PERFORMANCE_EVENTS.IS_LOW_PERFORMANCE
        : PERFORMANCE_EVENTS.IS_NOT_LOW_PERFORMANCE,
      payload: {
        avgTime: performanceData.avgTime,
        isMobile: performanceData.isMobile,
      },
    });
  }, [track, performanceData]);

  return (
    <PerformanceContext.Provider value={isLowPerformance}>
      {children}
    </PerformanceContext.Provider>
  );
};

const useIsPotato = () => {
  try {
    const isLowPerformance = useContext(PerformanceContext);
    return isLowPerformance;
  } catch (error) {
    return true;
  }
};

export default useIsPotato;
